import { forwardRef, type InputHTMLAttributes } from "react";
import { cn } from "@/lib/utils/cn";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, className, id, ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, "-");

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className="text-xs font-medium text-metro-ink-muted"
          >
            {label}
            {props.required && <span className="ml-0.5 text-red-500">*</span>}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          className={cn(
            "w-full rounded-md border px-3 py-2.5 text-sm text-metro-ink outline-none transition",
            "placeholder:text-metro-ink-subtle/50",
            "focus:border-metro-green-800 focus:ring-2 focus:ring-metro-green-800/10",
            error
              ? "border-red-400 focus:border-red-400 focus:ring-red-400/10"
              : "border-gray-200",
            props.disabled && "cursor-not-allowed bg-gray-50 opacity-60",
            className,
          )}
          {...props}
        />
        {hint && !error && (
          <p className="text-[11px] text-metro-ink-subtle">{hint}</p>
        )}
        {error && <p className="text-[11px] text-red-500">{error}</p>}
      </div>
    );
  },
);

Input.displayName = "Input";
export default Input;